import React, { useState, useEffect } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { authConfig } from '../config/amplify';
import { User, CognitoUserAttributes, ALLOWED_GROUPS } from '../types';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';

interface AuthWrapperProps {
  children: (props: { user: User | null; signOut: (() => void) | null }) => React.ReactNode;
  showAuthenticator: boolean;
  onHideAuthenticator: () => void;
}

// Helper to extract groups from ID token
function getGroupsFromToken(idToken: string): string[] {
  try {
    const payload = idToken.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded['cognito:groups'] || [];
  } catch {
    return [];
  }
}

// Helper to check if user has access (is in allowed group)
function hasAccess(groups: string[]): boolean {
  return groups.some(group => (ALLOWED_GROUPS as readonly string[]).includes(group));
}

// Generate a letter avatar SVG as a data URL
function generateLetterAvatar(name: string | undefined, email: string): string {
  // Get the first letter from name, or fall back to first letter of email
  const letter = (name?.[0] || email[0] || '?').toUpperCase();

  // Use a consistent color based on the email (simple hash)
  const colors = ['#C18E5E', '#A07045', '#6B8E23', '#8B7355', '#7B8D6B'];
  const colorIndex = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  const bgColor = colors[colorIndex];

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect width="100" height="100" fill="${bgColor}"/>
    <text x="50" y="50" dy="0.35em" text-anchor="middle" fill="white" font-family="sans-serif" font-size="45" font-weight="600">${letter}</text>
  </svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Helper to convert Cognito user to app User
async function getUserFromCognito(): Promise<User | null> {
  try {
    const [attributes, session] = await Promise.all([
      fetchUserAttributes() as unknown as Promise<CognitoUserAttributes>,
      fetchAuthSession()
    ]);

    const idToken = session.tokens?.idToken?.toString() || '';
    const groups = getGroupsFromToken(idToken);

    return {
      id: attributes.sub,
      email: attributes.email,
      name: attributes.name || attributes.email.split('@')[0],
      avatar: attributes.picture || generateLetterAvatar(attributes.name, attributes.email),
      groups,
    };
  } catch {
    return null;
  }
}

// Inner component that has access to auth state
const AuthWrapperInner: React.FC<AuthWrapperProps> = ({
  children,
  showAuthenticator,
  onHideAuthenticator
}) => {
  const { user: cognitoUser, signOut } = useAuthenticator((context) => [context.user, context.signOut]);
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Fetch user attributes when authenticated
  useEffect(() => {
    if (cognitoUser) {
      getUserFromCognito().then(setUser);
    } else {
      setUser(null);
    }
  }, [cognitoUser]);

  // Check for OAuth errors in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error && errorDescription) {
      const decodedDescription = decodeURIComponent(errorDescription);

      if (decodedDescription.includes('not authorized for social sign-up')) {
        setAuthError('Your email address is not authorized for social sign-in. Please contact support for access.');
      } else {
        setAuthError(`Sign-in failed: ${decodedDescription}`);
      }

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const dismissError = () => setAuthError(null);

  // If user is authenticated but not in allowed group, show access pending
  if (cognitoUser && user && !hasAccess(user.groups)) {
    return (
      <div className="min-h-screen bg-rutabaga-bg flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Access Pending</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Your account has been created, but you don't have access yet.
            Please contact an administrator to request access.
          </p>
          <div className="text-sm text-gray-500 mb-6">
            Signed in as: <strong>{user.email}</strong>
          </div>
          <button
            onClick={signOut}
            className="w-full bg-rutabaga-wood hover:bg-rutabaga-woodDark text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // If user is authenticated and has access, render children with user data
  if (cognitoUser && user) {
    return <>{children({ user, signOut })}</>;
  }

  // If not authenticated and not showing authenticator, render children with null user
  if (!showAuthenticator) {
    return <>{children({ user: null, signOut: null })}</>;
  }

  // Show authenticator when explicitly requested
  return (
    <div className="min-h-screen bg-rutabaga-bg flex flex-col items-center justify-center p-4">
      {authError && (
        <div className="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg max-w-md w-full">
          <div className="flex justify-between items-start">
            <div>
              <strong className="text-red-700">Sign-in Failed</strong>
              <p className="text-red-600 text-sm mt-1">{authError}</p>
            </div>
            <button
              onClick={dismissError}
              className="text-red-500 hover:text-red-700 text-xl leading-none"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Back button */}
      <div className="mb-4 w-full max-w-md">
        <button
          onClick={onHideAuthenticator}
          className="text-rutabaga-wood hover:text-rutabaga-woodDark flex items-center gap-1"
        >
          <span>&larr;</span> Back
        </button>
      </div>

      <div className="w-full max-w-md">
        <Authenticator
          socialProviders={authConfig.socialProviders}
          initialState={authConfig.initialState}
          loginMechanisms={authConfig.loginMechanisms}
          hideSignUp={!authConfig.enableSignUp}
        >
          {({ user: authUser }) => {
            if (authUser) {
              onHideAuthenticator();
            }
            return <>{children({ user, signOut })}</>;
          }}
        </Authenticator>
      </div>
    </div>
  );
};

export const AuthWrapper: React.FC<AuthWrapperProps> = (props) => {
  return (
    <Authenticator.Provider>
      <AuthWrapperInner {...props} />
    </Authenticator.Provider>
  );
};
